/**
 * Geo Consent —— 基于访问者国家的 GDPR 地域判断
 *
 * 数据来源链路：
 *   CloudFront（按访问者 IP 注入 CloudFront-Viewer-Country 头）
 *   → middleware.ts 转写为 busrom_geo cookie（eea | row）
 *   → ConsentModeDefaultScript / CookieConsentProvider 读取
 *
 * 策略：
 *   - eea（EU27 + 冰岛/列支敦士登/挪威 + 英国 + 瑞士）：GDPR/UK GDPR/FADP 适用，
 *     必须 opt-in，弹 Klaro 同意框，consent 默认 denied。
 *   - row（其余所有地区）：默认 granted，不弹窗，由 CookieConsentProvider
 *     自动写入全量同意 cookie。
 *   - 国家码缺失/未知（本地开发、直连 ALB）：fail-closed 按 eea 处理，保证合规。
 */

/** 需要 GDPR 式 opt-in 的国家/地区码（ISO 3166-1 alpha-2，大写） */
const GDPR_COUNTRIES = new Set([
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA 其余三国
  'IS', 'LI', 'NO',
  // UK GDPR
  'GB',
  // 瑞士 FADP
  'CH',
])

export type GeoRegion = 'eea' | 'row'

/** middleware 写入的 geo cookie 名 */
export const GEO_COOKIE_NAME = 'busrom_geo'

/** geo cookie 有效期：30 天（人会旅行，定期按最新 IP 重新判定） */
export const GEO_COOKIE_MAX_AGE = 30 * 24 * 60 * 60

/**
 * 国家码 → 地域。未知/缺失一律按 'eea'（fail-closed，合规优先）。
 */
export function countryToRegion(country: string | null | undefined): GeoRegion {
  if (!country) return 'eea'
  return GDPR_COUNTRIES.has(country.toUpperCase()) ? 'eea' : 'row'
}

/** 客户端读取 middleware 写入的 geo cookie；读不到返回 null（按 eea 处理） */
export function getGeoRegionFromCookie(): GeoRegion | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`${GEO_COOKIE_NAME}=(eea|row)`))
  return match ? (match[1] as GeoRegion) : null
}
